import React, { useState, InputHTMLAttributes } from "react";
import { defineMessages, useIntl } from "react-intl";
import * as Form from "@radix-ui/react-form";
import { Check, Mail, Plus, X, TriangleAlert, Info } from "@labxchange/icons";

import { srOnly } from "@labxchange/design_tokens";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import { Tooltip } from "@labxchange/ui-components/lib/Tooltip";

import {
  containerClass,
  labelClass,
  inputWrapperClass,
  inputWrapperBorderlessClass,
  inputClass,
  inputBorderlessClass,
  iconClass,
  helpTextClass,
  errorTextClass,
  successTextClass,
  warningTextClass,
  feedbackIconClass,
  hintIconClass,
  suffixTextClass,
} from "./TextInput.css";

const messages = defineMessages({
  feedbackLabel: {
    id: "LxTextInput.feedbackLabel",
    defaultMessage: "Feedback",
    description:
      "Accessible label for the feedback tooltip button shown next to an input after submission.",
  },
});

export type TextInputIconType =
  | "check"
  | "message"
  | "plus"
  | "cross"
  | "warning";

const ICON_MAP = {
  check: Check,
  message: Mail,
  plus: Plus,
  cross: X,
  warning: TriangleAlert,
} as const;

const STATE_ICONS = {
  success: Check,
  error: X,
  warning: TriangleAlert,
} as const;

const FEEDBACK_TOOLTIP_VARIANT: Record<
  "success" | "error" | "warning",
  "success" | "fail" | "dark"
> = {
  success: "success",
  warning: "dark",
  error: "fail",
} as const;

export interface ValidationMessage {
  match: Form.FormMessageProps["match"];
  message: string | { id: string; defaultMessage: string };
}

/**
 * LxTextInput - A text input component built on Radix UI Form primitives.
 *
 * IMPORTANT: This component requires a `<Form.Root>` ancestor in the component tree.
 * The Form.Root provides the form context needed for validation and accessibility features.
 *
 * @example
 * ```tsx
 * import * as Form from "@radix-ui/react-form";
 *
 * <Form.Root>
 *   <LxTextInput name="email" label="Email" />
 *   <Form.Submit>Submit</Form.Submit>
 * </Form.Root>
 * ```
 */

export type TextInputVariant = "default" | "borderless";

export interface LxTextInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "prefix" | "name" | "placeholder"
> {
  label?: string | { id: string; defaultMessage: string };
  helpText?: string | { id: string; defaultMessage: string };
  errorText?: string | { id: string; defaultMessage: string };
  placeholder?: string | { id: string; defaultMessage: string };
  prefix?: TextInputIconType;
  suffix?: TextInputIconType;
  state?: "default" | "success" | "error" | "warning";
  successText?: string | { id: string; defaultMessage: string };
  warningText?: string | { id: string; defaultMessage: string };
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  type?: string;
  id?: string;
  name: string;
  validationMessages?: ValidationMessage[];
  validateOnChange?: boolean;
  variant?: TextInputVariant;
  /** Feedback text to show in tooltip when hovering over feedback icon. When provided with showFeedback, displays tooltip with success/fail variant */
  feedback?: string;
  /** Whether to show the feedback icon. When true without feedback, shows only the icon. When true with feedback, shows icon with tooltip */
  showFeedback?: boolean;
  /** Hint text to show in tooltip when hovering over hint icon (shown when no value) */
  hint?: string;
  /** Size of the hint icon (default: 18, use 24 for table cells) */
  hintIconSize?: number;
  /** Non-removable text suffix displayed after the input value (e.g., "%", "kg") */
  suffixText?: string;
}

export const LxTextInput = React.forwardRef<HTMLInputElement, LxTextInputProps>(
  (
    {
      label,
      helpText,
      errorText,
      successText,
      warningText,
      prefix,
      suffix,
      state = "default",
      value,
      onChange,
      disabled = false,
      placeholder,
      type = "text",
      id,
      name,
      validationMessages,
      validateOnChange = true,
      variant = "default",
      feedback,
      showFeedback = false,
      hint,
      hintIconSize = 18,
      suffixText,
      ...rest
    },
    ref
  ) => {
    const intl = useIntl();
    const [isFocused, setIsFocused] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [internalValue, setInternalValue] = useState(value || "");

    const generatedId = React.useId();
    const inputId = id || generatedId;
    const descriptionId = `${inputId}-description`;

    // Feedback shown in the tooltip is also exposed as visually-hidden text and
    // linked to the <input> via aria-describedby so screen readers read it.
    const hasFeedbackText = !!feedback && showFeedback;
    const feedbackTextId = `${inputId}-feedback`;

    const currentValue = value !== undefined ? value : internalValue;
    const hasValue = currentValue.length > 0;
    const showError = state === "error" && errorText;
    const showSuccess = state === "success" && successText;
    const showWarning = state === "warning" && warningText;
    let displayHelpText: LxTextInputProps["helpText"];
    if (showError) {
      displayHelpText = errorText;
    } else if (showWarning) {
      displayHelpText = warningText;
    } else if (showSuccess) {
      displayHelpText = successText;
    } else {
      displayHelpText = helpText;
    }

    const tooltipVariant: "success" | "fail" | "dark" =
      state === "success" || state === "error" || state === "warning"
        ? FEEDBACK_TOOLTIP_VARIANT[state]
        : "fail";

    const translatedPlaceholder =
      typeof placeholder === "object"
        ? intl.formatMessage(placeholder)
        : placeholder;

    const getDataState = () => {
      if (state === "error") return "error";
      if (state === "success") return "success";
      if (state === "warning") return "warning";
      if (disabled) return "disabled";
      if (isFocused) return "typing";
      return "default";
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(event.target.value);
      onChange?.(event);
    };

    const handleBlur = () => {
      setIsFocused(false);
      setIsDirty(true);
    };

    const PrefixIcon = prefix ? ICON_MAP[prefix] : null;
    const SuffixIcon = suffix ? ICON_MAP[suffix] : null;
    const AutoStateIcon =
      state === "success" || state === "error" || state === "warning"
        ? STATE_ICONS[state]
        : null;
    const dataState = getDataState();

    return (
      <Form.Field
        name={name}
        serverInvalid={state === "error"}
        className={containerClass}
      >
        {label && (
          <Form.Label className={labelClass} data-disabled={disabled}>
            <RichText value={label} />
          </Form.Label>
        )}
        <div
          className={
            variant === "borderless"
              ? inputWrapperBorderlessClass
              : inputWrapperClass
          }
          data-state={dataState}
          data-disabled={disabled}
          data-has-value={hasValue}
        >
          {PrefixIcon && (
            <div className={iconClass} aria-hidden="true">
              <PrefixIcon size={24} />
            </div>
          )}
          <Form.Control asChild>
            <input
              ref={ref}
              id={inputId}
              type={type}
              value={value}
              onChange={handleChange}
              disabled={disabled}
              placeholder={translatedPlaceholder}
              className={
                variant === "borderless" ? inputBorderlessClass : inputClass
              }
              onFocus={() => setIsFocused(true)}
              onBlur={handleBlur}
              data-state={dataState}
              aria-invalid={state === "error" || undefined}
              aria-describedby={
                [
                  displayHelpText ? descriptionId : undefined,
                  hasFeedbackText ? feedbackTextId : undefined,
                ]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
              {...rest}
            />
          </Form.Control>
          {suffixText && (
            <span className={suffixTextClass} aria-hidden="true">
              {suffixText}
            </span>
          )}
          {variant === "borderless" ? (
            // In borderless mode, always reserve space to prevent layout shift
            hint && !hasValue ? (
              <Tooltip content={hint} variant="dark" placement="bottom">
                <span className={hintIconClass}>
                  <Info size={hintIconSize} />
                </span>
              </Tooltip>
            ) : (SuffixIcon || AutoStateIcon) &&
              (showFeedback || state === "warning") ? (
              (() => {
                const Icon = SuffixIcon ?? AutoStateIcon;
                return feedback ? (
                  <Tooltip
                    content={feedback}
                    variant={tooltipVariant}
                    placement="bottom"
                    showIcon
                    ariaLabel={intl.formatMessage(messages.feedbackLabel)}
                  >
                    <span className={feedbackIconClass}>
                      {Icon && <Icon size={24} />}
                    </span>
                  </Tooltip>
                ) : (
                  <span className={feedbackIconClass}>
                    {Icon && <Icon size={24} />}
                  </span>
                );
              })()
            ) : (
              <></>
            )
          ) : (
            <>
              {(SuffixIcon || AutoStateIcon) &&
                (() => {
                  const Icon = SuffixIcon ?? AutoStateIcon;
                  return showFeedback ? (
                    feedback ? (
                      <Tooltip
                        content={feedback}
                        variant={tooltipVariant}
                        placement="bottom"
                        showIcon
                        ariaLabel={intl.formatMessage(messages.feedbackLabel)}
                      >
                        <span className={feedbackIconClass}>
                          {Icon && <Icon size={24} />}
                        </span>
                      </Tooltip>
                    ) : (
                      <span className={feedbackIconClass}>
                        {Icon && <Icon size={24} />}
                      </span>
                    )
                  ) : SuffixIcon ? (
                    <div className={iconClass} aria-hidden="true">
                      <SuffixIcon size={24} />
                    </div>
                  ) : null;
                })()}
              {hint && !hasValue && (
                <Tooltip content={hint} variant="dark" placement="bottom">
                  <span className={hintIconClass}>
                    <Info size={hintIconSize} />
                  </span>
                </Tooltip>
              )}
            </>
          )}
        </div>
        {hasFeedbackText && (
          <span id={feedbackTextId} className={srOnly}>
            <RichText text={feedback} />
          </span>
        )}
        {isDirty &&
          validationMessages?.map((validation, index) => (
            <Form.Message
              key={index}
              match={validation.match}
              className={errorTextClass}
              forceMatch={validateOnChange}
            >
              {typeof validation.message === "object"
                ? intl.formatMessage(validation.message)
                : validation.message}
            </Form.Message>
          ))}
        {displayHelpText && (
          <p
            id={descriptionId}
            className={
              showError
                ? errorTextClass
                : showWarning
                  ? warningTextClass
                  : showSuccess
                    ? successTextClass
                    : helpTextClass
            }
            data-disabled={disabled}
            role={showError || showWarning ? "alert" : undefined}
          >
            <RichText value={displayHelpText} />
          </p>
        )}
      </Form.Field>
    );
  }
);

LxTextInput.displayName = "LxTextInput";
